import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { getActiveEvents } from "../../api/events";
import EventEditModal from "../../components/EventEditModal";
import EventImagePositioner from "../EventImagePositioner";
import { formatSmartDate } from "../../utils/formatDate";
import { buildEventDetailsPath } from "../../utils/eventRoutes";
import "./style.css";

const EventTypeBadge = ({ eventType, isPast }) => {
  const isOnline = eventType === "Online";
  const badgeClass = isPast
    ? "new-events-new__badge new-events-new__badge--past"
    : `new-events-new__badge ${isOnline ? "new-events-new__badge--online" : ""}`;
  return (
    <div className={badgeClass}>
      <span className="new-events-new__badge-dot"></span>
      {isOnline ? "Eveniment Online" : "Eveniment fizic"}
    </div>
  );
};

/**
 * Parses a Romanian date string like "5 iunie 2026" or "5-7 iunie 2026"
 * Returns the END date of the range (last day) as a Date object.
 */
const parseEventEndDate = (dateStr) => {
  if (!dateStr) return null;
  const months = {
    ianuarie: 0, februarie: 1, martie: 2, aprilie: 3, mai: 4, iunie: 5,
    iulie: 6, august: 7, septembrie: 8, octombrie: 9, noiembrie: 10, decembrie: 11,
  };

  // Normalize: remove extra spaces
  const cleaned = dateStr.trim().toLowerCase();

  // Try "5-7 iunie 2026" or "5 - 7 iunie 2026"
  const rangeMatch = cleaned.match(/(\d{1,2})\s*[-–]\s*(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (rangeMatch) {
    const day = parseInt(rangeMatch[2]);
    const month = months[rangeMatch[3]];
    const year = parseInt(rangeMatch[4]);
    if (month !== undefined) return new Date(year, month, day, 23, 59, 59);
  }

  // Try "5 iunie 2026 - 7 iulie 2026"
  const fullRangeMatch = cleaned.match(/\d{1,2}\s+\w+\s+\d{4}\s*[-–]\s*(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (fullRangeMatch) {
    const day = parseInt(fullRangeMatch[1]);
    const month = months[fullRangeMatch[2]];
    const year = parseInt(fullRangeMatch[3]);
    if (month !== undefined) return new Date(year, month, day, 23, 59, 59);
  }

  // Try simple "5 iunie 2026"
  const simpleMatch = cleaned.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (simpleMatch) {
    const day = parseInt(simpleMatch[1]);
    const month = months[simpleMatch[2]];
    const year = parseInt(simpleMatch[3]);
    if (month !== undefined) return new Date(year, month, day, 23, 59, 59);
  }

  return null;
};

const NewEventsList = () => {
  const [dynamicEvents, setDynamicEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("viitoare");
  const [editingEvent, setEditingEvent] = useState(null);
  const staticEvents = getActiveEvents();
  const { user } = useSelector((store) => store);

  const isAdmin = user?.user?.email === process.env.REACT_APP_ADMIN ||
    user?.user?.email === process.env.REACT_APP_ARI_ADMIN;

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE}/campaign/get`
        );
        const data = Array.isArray(response.data) ? response.data : [];
        const mapped = data
          .filter((e) => !e.skipped)
          .map((e) => ({
          ...e,
          id: e.id || e.docId,
          title: e.title,
          location: e.location || "",
          date: e.timeInterval ? e.timeInterval.split(",")[0]?.trim() : "",
          time: e.timeInterval ? e.timeInterval.split(",")[1]?.trim() : "",
          link: buildEventDetailsPath(e),
          dateFrom: e.dateFrom || "",
          dateTo: e.dateTo || "",
          eventType: e.eventType || "Fizic",
          coverImage: e.coverImage || "",
          imagePositionX: e.imagePositionX ?? 50,
          imagePositionY: e.imagePositionY ?? 50,
          imageZoom: e.imageZoom ?? 1,
        }));
        setDynamicEvents(mapped);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Merge static + dynamic events, remove duplicates by id
  const allEvents = [...staticEvents];
  dynamicEvents.forEach((de) => {
    if (!allEvents.find((se) => se.id === de.id)) {
      allEvents.push(de);
    }
  });

  // Split into upcoming and past events
  const now = new Date();
  const upcomingEvents = [];
  const pastEvents = [];

  allEvents.forEach((event) => {
    // Use dateTo (ISO string) if available, otherwise parse from the date string
    let endDate = null;
    if (event.dateTo) {
      endDate = new Date(event.dateTo);
    } else {
      endDate = parseEventEndDate(event.date);
    }

    if (!endDate || endDate >= now) {
      upcomingEvents.push(event);
    } else {
      pastEvents.push(event);
    }
  });

  const displayedEvents = activeTab === "viitoare" ? upcomingEvents : pastEvents;

  if (!loading && !allEvents.length) {
    return (
      <section className="new-events-new new-events-list">
        <div className="new-events-new__inner">
          <p className="new-events-list__empty">
            Momentan nu sunt evenimente programate. Revino curând — pregătim
            ediția următoare.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="new-events-new new-events-list">
      <div className="new-events-new__inner">
        <div className="new-events-new__header">
          <span className="new-events-new__pre-title">CALENDAR 2026</span>
          <h2 className="new-events-new__title">Evenimente</h2>
          <p className="new-events-new__subtitle">ne întâlnim în orașul tău</p>
          <p className="new-events-new__desc">
            
Participă la experiențe create pentru a te reconecta cu tine însăți, pentru a descoperi puterea uleiurilor esențiale și pentru a încetini ritmul agitat al vieții de zi cu zi.
          </p>
        </div>

        {/* Tabs: Viitoare / Trecute */}
        <div className="new-events-list__tabs">
          <button
            className={`new-events-list__tab ${activeTab === "viitoare" ? "new-events-list__tab--active" : ""}`}
            onClick={() => setActiveTab("viitoare")}
          >
            Viitoare
            {upcomingEvents.length > 0 && (
              <span className="new-events-list__tab-count">{upcomingEvents.length}</span>
            )}
          </button>
          <button
            className={`new-events-list__tab ${activeTab === "trecute" ? "new-events-list__tab--active" : ""}`}
            onClick={() => setActiveTab("trecute")}
          >
            Trecute
            {pastEvents.length > 0 && (
              <span className="new-events-list__tab-count">{pastEvents.length}</span>
            )}
          </button>
        </div>

        {displayedEvents.length === 0 ? (
          <p className="new-events-list__empty-tab">
            {activeTab === "viitoare"
              ? "Nu sunt evenimente viitoare programate momentan."
              : "Nu există evenimente trecute."
            }
          </p>
        ) : (
          <div className="new-events-new__grid new-events-list__grid">
            {displayedEvents.map((event) => (
              <div key={event.id} className="new-events-new__card" style={{ position: 'relative' }}>
                {/* Edit button - admin only */}
                {isAdmin && (
                  <button
                    className="new-events-new__edit-btn"
                    onClick={() => setEditingEvent(event)}
                    title="Editează"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
                {/* Cover photo with overlay */}
                <div className="new-events-new__card-photo">
                  <EventImagePositioner
                    eventId={event.id}
                    src={event.coverImage}
                    alt={event.title}
                    initialX={event.imagePositionX ?? 50}
                    initialY={event.imagePositionY ?? 50}
                    initialZoom={event.imageZoom ?? 1}
                    isAdmin={isAdmin}
                  />
                  <div className="new-events-new__card-overlay">
                    <EventTypeBadge eventType={event.eventType} isPast={activeTab === "trecute"} />
                    <div className="new-events-new__card-overlay-text">
                      <h3 className="new-events-new__card-title">{event.title}</h3>
                      {event.subtitle && (
                        <p className="new-events-new__card-subtitle">{event.subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="new-events-new__card-body">

                  <div className="new-events-new__details">
                    <div className="new-events-new__detail">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>{event.location}</span>
                    </div>
                    <div className="new-events-new__detail">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>{(event.dateFrom || event.dateTo) ? formatSmartDate(event.dateFrom, event.dateTo) : event.date}</span>
                    </div>
                    {event.time && (
                      <div className="new-events-new__detail">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>{event.time}</span>
                      </div>
                    )}
                  </div>

                  {activeTab === "viitoare" ? (
                    <Link to={event.link} className="new-events-new__btn">
                      Rezervă Locul Tău
                    </Link>
                  ) : (
                    <Link to={`${event.link}/galerie`} className="new-events-new__btn new-events-new__btn--gallery">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      Vezi galerie
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {editingEvent && (
          <EventEditModal
            event={editingEvent}
            onClose={() => setEditingEvent(null)}
            onSaved={() => window.location.reload()}
          />
        )}
      </div>
    </section>
  );
};

export default NewEventsList;
